import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Badge } from "primereact/badge";
import { TabView, TabPanel } from "primereact/tabview";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { toastify } from "../../../../../utils/toast";
import { communicationBaseRoute } from "../../../../../utils/urls";

function ViewEmailModal({
  visible,
  onHide,
  email,
  facilityId,
  onEmailUpdated,
  onEmailDeleted,
}) {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    to: "",
    subject: "",
    text: "",
    html: "",
  });

  // Initialize form data when email changes
  React.useEffect(() => {
    if (email?.email) {
      setFormData({
        to: email.email.to || "",
        subject: email.email.subject || "",
        text: email.email.text || "",
        html: email.email.html || "",
      });
    }
  }, [email]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Update email in queue
  const updateEmail = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `${communicationBaseRoute}/${facilityId}/email/${email._id}`,
        "PUT",
        formData,
      );

      if (response.success) {
        toastify("Email updated successfully", "success");
        setEditing(false);
        onEmailUpdated();
      } else {
        throw new Error(response.error || "Failed to update email");
      }
    } catch (error) {
      toastify("Error updating email: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Delete email from queue
  const deleteEmail = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `${communicationBaseRoute}/${facilityId}/email/${email._id}`,
        "DELETE",
      );

      if (response.success) {
        toastify("Email deleted successfully", "success");
        onEmailDeleted();
        onHide();
      } else {
        throw new Error(response.error || "Failed to delete email");
      }
    } catch (error) {
      toastify("Error deleting email: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditing(false);
    // Reset form data
    if (email?.email) {
      setFormData({
        to: email.email.to || "",
        subject: email.email.subject || "",
        text: email.email.text || "",
        html: email.email.html || "",
      });
    }
  };

  // Modal header
  const modalHeader = (
    <div className="d-flex align-items-center justify-content-between w-100">
      <div className="d-flex align-items-center">
        <i className="ti ti-mail me-2"></i>
        <span>Email Details</span>
      </div>
      <div className="d-flex align-items-center gap-2">
        {email && (
          <Badge
            value={email.status || "pending"}
            severity={email.status === "pending" ? "warning" : "info"}
          />
        )}
      </div>
    </div>
  );

  // Modal footer
  const modalFooter = (
    <div className="d-flex justify-content-between w-100">
      <div>
        <Button
          label="Delete"
          icon="ti ti-trash"
          className="p-button-danger p-button-outlined"
          onClick={deleteEmail}
          loading={loading}
        />
      </div>
      <div className="d-flex gap-2">
        {editing ? (
          <>
            <Button
              label="Cancel"
              icon="ti ti-x"
              className="p-button-text"
              onClick={cancelEdit}
              disabled={loading}
            />
            <Button
              label="Save Changes"
              icon="ti ti-check"
              onClick={updateEmail}
              loading={loading}
            />
          </>
        ) : (
          <>
            <Button
              label="Close"
              icon="ti ti-x"
              className="p-button-text"
              onClick={onHide}
            />
            <Button
              label="Edit"
              icon="ti ti-edit"
              onClick={() => setEditing(true)}
            />
          </>
        )}
      </div>
    </div>
  );

  if (!email) return null;

  return (
    <Dialog
      visible={visible}
      style={{ width: "90vw", maxWidth: "800px" }}
      header={modalHeader}
      footer={modalFooter}
      onHide={onHide}
      modal
      maximizable
    >
      <div className="email-details">
        {/* Email Metadata */}
        <Card className="mb-3">
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  <i className="ti ti-calendar me-2"></i>
                  Queued At
                </label>
                <div className="text-muted">{formatDate(email.createdAt)}</div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  <i className="ti ti-id me-2"></i>
                  Queue ID
                </label>
                <div className="text-muted font-monospace">{email._id}</div>
              </div>
            </div>
          </div>
        </Card>

        {/* Email Content */}
        <Card>
          <TabView>
            <TabPanel header="Email Content" leftIcon="ti ti-mail me-2">
              <div className="email-content">
                {/* Recipient */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    <i className="ti ti-user me-2"></i>
                    To
                  </label>
                  {editing ? (
                    <InputText
                      value={formData.to}
                      onChange={(e) => handleInputChange("to", e.target.value)}
                      className="w-100"
                      placeholder="Recipient email address"
                    />
                  ) : (
                    <div className="form-control-plaintext">
                      {email.email?.to || "-"}
                    </div>
                  )}
                </div>

                {/* Subject */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    <i className="ti ti-message me-2"></i>
                    Subject
                  </label>
                  {editing ? (
                    <InputText
                      value={formData.subject}
                      onChange={(e) =>
                        handleInputChange("subject", e.target.value)
                      }
                      className="w-100"
                      placeholder="Email subject"
                    />
                  ) : (
                    <div className="form-control-plaintext">
                      {email.email?.subject || "-"}
                    </div>
                  )}
                </div>

                {/* Text Content */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    <i className="ti ti-file-text me-2"></i>
                    Text Content
                  </label>
                  {editing ? (
                    <InputTextarea
                      value={formData.text}
                      onChange={(e) =>
                        handleInputChange("text", e.target.value)
                      }
                      rows={8}
                      className="w-100"
                      placeholder="Email text content"
                    />
                  ) : (
                    <div className="form-control-plaintext">
                      <pre className="text-wrap">
                        {email.email?.text || "-"}
                      </pre>
                    </div>
                  )}
                </div>

                {/* HTML Content */}
                {(email.email?.html || editing) && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      <i className="ti ti-code me-2"></i>
                      HTML Content
                    </label>
                    {editing ? (
                      <InputTextarea
                        value={formData.html}
                        onChange={(e) =>
                          handleInputChange("html", e.target.value)
                        }
                        rows={6}
                        className="w-100 font-monospace"
                        placeholder="Email HTML content (optional)"
                      />
                    ) : (
                      <div className="form-control-plaintext">
                        <pre className="text-wrap font-monospace text-muted">
                          {email.email?.html || "-"}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </TabPanel>

            {/* HTML Preview Tab */}
            {email.email?.html && !editing && (
              <TabPanel header="HTML Preview" leftIcon="ti ti-eye me-2">
                <div className="email-preview border rounded p-3">
                  <div
                    dangerouslySetInnerHTML={{ __html: email.email.html }}
                    style={{
                      maxHeight: "400px",
                      overflowY: "auto",
                      background: "#fff",
                      padding: "1rem",
                    }}
                  />
                </div>
              </TabPanel>
            )}

            {/* Metadata Tab */}
            <TabPanel header="Metadata" leftIcon="ti ti-info-circle me-2">
              <div className="metadata-content">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Type</label>
                      <div className="text-muted">{email.type || "email"}</div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Status</label>
                      <Badge
                        value={email.status || "pending"}
                        severity={
                          email.status === "pending" ? "warning" : "info"
                        }
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Facility ID
                      </label>
                      <div className="text-muted font-monospace">
                        {email.facilityId}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Created At
                      </label>
                      <div className="text-muted">
                        {formatDate(email.createdAt)}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Updated At
                      </label>
                      <div className="text-muted">
                        {formatDate(email.updatedAt)}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Queue ID</label>
                      <div className="text-muted font-monospace">
                        {email._id}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Email Properties */}
                {email.email?.senderName && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Sender Name
                    </label>
                    <div className="text-muted">{email.email.senderName}</div>
                  </div>
                )}
              </div>
            </TabPanel>
          </TabView>
        </Card>
      </div>
    </Dialog>
  );
}

export default ViewEmailModal;
